/**
 * @fileoverview Newsletter Validator Web Component
 * -----------------------------------------------------------------------------
 * Email validation for newsletter subscription forms with real-time feedback.
 */

/**
 * NewsletterValidator class
 * 
 * @class NewsletterValidator
 * @extends {HTMLElement}
 * @property {HTMLFormElement|null} form
 * @property {HTMLInputElement|null} emailInput
 * @property {HTMLButtonElement|null} submitButton
 * @property {HTMLElement|null} errorMessageContainer
 * @property {boolean} hasBeenFocused
 * @property {string|null} resultParam
 */
class NewsletterValidator extends HTMLElement {
  /**
   * Initialize the validator component
   * @constructor
   */
  constructor() {
    super();
    
    // Store the result parameter if available
    this.resultParam = this.getQueryParameter('result');
  }

  /**
   * Lifecycle callback when element is added to the DOM
   * Sets up form elements, event listeners, and initial state
   * 
   * @returns {void}
   */
  connectedCallback() {
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input[type="email"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    
    if (!this.form || !this.emailInput || !this.submitButton) return;
    
    this.createErrorMessageContainer();
    this.hasBeenFocused = false;
    
    this.emailInput.addEventListener('input', this.validateEmailField.bind(this));
    this.emailInput.addEventListener('focus', () => {
      this.hasBeenFocused = true;
    }, { once: true });
    
    this.emailInput.addEventListener('blur', () => {
      if (this.hasBeenFocused) {
        this.validateEmail();
      }
    });
    
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.checkResult();
  }

  /**
   * Extract a query parameter value from the current URL
   * 
   * @param {string} param - The name of the query parameter to retrieve
   * @returns {string|null} The parameter value or null if not found
   */
  getQueryParameter(param) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    return params.get(param);
  }

  /**
   * Check if the URL has a 'result=success' parameter and show success message
   * 
   * @returns {void}
   */
  checkResult() {
    if (this.resultParam === 'success') {
      // If there's already a success message in the DOM, make sure it's visible
      const existingSuccessMessage = this.querySelector('.newsletter-form__message--success');
      if (existingSuccessMessage) {
        existingSuccessMessage.setAttribute('aria-hidden', 'false');
        existingSuccessMessage.focus();
      }
    }
  }

  /**
   * Creates an error message container if one doesn't already exist
   * 
   * @returns {void}
   */
  createErrorMessageContainer() {
    if (!this.querySelector('.newsletter-form__error-message')) {
      const errorContainer = document.createElement('div');
      errorContainer.classList.add('newsletter-form__error-message');
      errorContainer.style.display = 'none';
      errorContainer.setAttribute('role', 'alert');
      
      const fieldWrapper = this.emailInput.closest('.field');
      if (fieldWrapper) {
        fieldWrapper.parentNode.insertBefore(errorContainer, fieldWrapper.nextSibling);
      }
      
      this.errorMessageContainer = errorContainer;
    } else {
      this.errorMessageContainer = this.querySelector('.newsletter-form__error-message');
    }
  }

  /**
   * Handles input event on email field with debouncing
   * 
   * @returns {void}
   */
  validateEmailField() {
    if (!this.hasBeenFocused) return;
    
    clearTimeout(this.validationTimeout);
    this.validationTimeout = setTimeout(() => {
      this.validateEmail();
    }, 300);
  }

  /**
   * Validates the email input against a standard email regex pattern
   * 
   * @returns {boolean} True if email is valid, false otherwise
   */
  validateEmail() {
    const email = this.emailInput.value.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const emptyEmailError = this.getAttribute('data-empty-error') || 'Please enter an email address';
    const invalidEmailError = this.getAttribute('data-invalid-error') || 'Please enter a valid email address';
    
    if (email === '') {
      this.showError(emptyEmailError);
      this.toggleButtonState(false);
      return false;
    } else if (!emailPattern.test(email)) {
      this.showError(invalidEmailError);
      this.toggleButtonState(false);
      return false;
    }
    
    this.hideError();
    this.toggleButtonState(true);
    return true;
  }

  /**
   * Enables or disables the submit button based on validation state
   * 
   * @param {boolean} isValid - Whether the email input is valid
   * @returns {void}
   */
  toggleButtonState(isValid) {
    if (this.submitButton) {
      if (isValid || !this.hasBeenFocused) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('disabled');
      } else {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('disabled');
      }
    }
  }

  /**
   * Displays an error message and adds error styling to the input
   * 
   * @param {string} message - The error message to display
   * @returns {void}
   */
  showError(message) {
    if (this.errorMessageContainer) {
      this.errorMessageContainer.textContent = message;
      this.errorMessageContainer.style.display = 'block';
      this.emailInput.classList.add('field__input--error');
    }
  }

  /**
   * Hides the error message and removes error styling from the input
   * 
   * @returns {void}
   */
  hideError() {
    if (this.errorMessageContainer) {
      this.errorMessageContainer.style.display = 'none';
      this.emailInput.classList.remove('field__input--error');
    }
  }

  /**
   * Form submission event handler
   * 
   * @param {Event} event - The submit event
   * @returns {void}
   */
  onSubmit(event) {
    this.hasBeenFocused = true;
    
    if (!this.validateEmail()) {
      event.preventDefault();
    }
  }
}

customElements.define('newsletter-validator', NewsletterValidator);
